var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Divider, FormControlLabel, InputBase, List, ListItemButton, Popover, Typography, styled, } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
export var MAX_INPUT_LENGTH = 256;
var DATE_FORMAT = 'YYYY-MM-DD';
var toMoment = function (value) { return (value ? moment(value, DATE_FORMAT) : null); };
var toDateString = function (value) { return ((value === null || value === void 0 ? void 0 : value.isValid()) ? value.format(DATE_FORMAT) : null); };
var MenuContainer = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        minWidth: theme.typography.pxToRem(220),
        maxWidth: theme.typography.pxToRem(320),
    });
});
var SortItem = styled(ListItemButton, {
    shouldForwardProp: function (prop) { return prop !== 'active'; },
})(function (_a) {
    var theme = _a.theme, active = _a.active;
    return ({
        gap: theme.spacing(3),
        padding: theme.spacing(2, 4),
        backgroundColor: active ? theme.palette.primary.light : 'transparent',
        color: theme.palette.primary.main,
        fontSize: theme.typography.body2.fontSize,
        '&:hover': {
            backgroundColor: active ? theme.palette.primary.light : theme.palette.action.hover,
        },
    });
});
var FilterLabel = styled(Typography)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(1, 4, 0),
        color: theme.palette.text.secondary,
        fontSize: theme.typography.caption.fontSize,
        fontWeight: theme.typography.fontWeightBold,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    });
});
var SearchInput = styled(InputBase)(function (_a) {
    var theme = _a.theme;
    return ({
        padding: theme.spacing(1, 4),
        fontSize: theme.typography.body2.fontSize,
        width: '100%',
    });
});
var ValueList = styled(List)(function (_a) {
    var theme = _a.theme;
    return ({
        maxHeight: theme.typography.pxToRem(200),
        overflowY: 'auto',
        padding: theme.spacing(1, 0),
    });
});
var ValueItem = styled(FormControlLabel)(function (_a) {
    var theme = _a.theme;
    return ({
        margin: 0,
        padding: theme.spacing(0.5, 3),
        width: '100%',
        '&:hover': { backgroundColor: theme.palette.action.hover },
        '& .MuiFormControlLabel-label': {
            fontSize: theme.typography.body2.fontSize,
            display: 'flex',
            gap: theme.spacing(1),
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    });
});
var CountLabel = styled('span')(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.text.secondary,
        flexShrink: 0,
    });
});
var DateRangeBox = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        padding: theme.spacing(3, 4, 2),
    });
});
var ClearRow = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
});
// All values selected means "no filter", so store it as null.
var normalizeSelectedValues = function (selectedValues, allValues) {
    if (selectedValues !== null && selectedValues.length === allValues.length) {
        return null;
    }
    return selectedValues;
};
export var ColumnFilterMenu = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var anchorEl = _a.anchorEl, open = _a.open, onClose = _a.onClose, sortable = _a.sortable, kind = _a.kind, currentSort = _a.currentSort, allValues = _a.allValues, currentSelectedValues = _a.currentSelectedValues, currentContains = _a.currentContains, currentDateRange = _a.currentDateRange, onApply = _a.onApply;
    var t = useTranslation().t;
    var _h = useState(''), optionQuery = _h[0], setOptionQuery = _h[1];
    useEffect(function () {
        if (!open)
            setOptionQuery('');
    }, [open]);
    var visibleValues = useMemo(function () {
        return optionQuery
            ? allValues.filter(function (v) { var _a; return ((_a = v.label) !== null && _a !== void 0 ? _a : v.value).toLowerCase().includes(optionQuery.toLowerCase()); })
            : allValues;
    }, [allValues, optionQuery]);
    var isValueChecked = function (value) { return currentSelectedValues === null || currentSelectedValues.includes(value); };
    var selectAllChecked = currentSelectedValues === null;
    var selectAllIndeterminate = currentSelectedValues !== null && currentSelectedValues.length > 0;
    var applyValues = function (selectedValues) {
        onApply({ selectedValues: normalizeSelectedValues(selectedValues, allValues) });
    };
    var handleSort = function (sort) {
        onApply({ sort: currentSort === sort ? null : sort });
    };
    var handleSelectAll = function () {
        applyValues(selectAllChecked || selectAllIndeterminate ? [] : null);
    };
    var handleToggleValue = function (value) {
        if (currentSelectedValues === null) {
            applyValues(allValues.map(function (v) { return v.value; }).filter(function (v) { return v !== value; }));
        }
        else {
            var next = currentSelectedValues.includes(value)
                ? currentSelectedValues.filter(function (v) { return v !== value; })
                : __spreadArray(__spreadArray([], currentSelectedValues, true), [value], false);
            applyValues(next);
        }
    };
    return (_jsx(Popover, __assign({ open: open, anchorEl: anchorEl, onClose: onClose, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' } }, { children: _jsxs(MenuContainer, { children: [sortable && (_jsxs(_Fragment, { children: [_jsxs(SortItem, __assign({ active: currentSort === 'asc', onClick: function () { return handleSort('asc'); } }, { children: [_jsx(FontAwesomeIcon, { icon: "circle-arrow-up", size: "sm" }), t('table.sortAtoZ', { defaultValue: 'Sort A to Z' })] })), _jsxs(SortItem, __assign({ active: currentSort === 'desc', onClick: function () { return handleSort('desc'); } }, { children: [_jsx(FontAwesomeIcon, { icon: "circle-arrow-down", size: "sm" }), t('table.sortZtoA', { defaultValue: 'Sort Z to A' })] }))] })), kind === 'text' && (_jsxs(_Fragment, { children: [sortable && _jsx(Divider, {}), _jsx(FilterLabel, { children: t('table.filter', { defaultValue: 'Filter' }) }), _jsx(SearchInput, { autoFocus: true, placeholder: t('table.filterContains', { defaultValue: 'Contains...' }), value: currentContains !== null && currentContains !== void 0 ? currentContains : '', onChange: function (e) { return onApply({ contains: e.target.value || null }); }, inputProps: {
                                'aria-label': t('table.filterContains', { defaultValue: 'Contains' }),
                                maxLength: MAX_INPUT_LENGTH,
                            } })] })), kind === 'enum' && (_jsxs(_Fragment, { children: [sortable && _jsx(Divider, {}), _jsx(FilterLabel, { children: t('table.filter', { defaultValue: 'Filter' }) }), _jsx(SearchInput, { placeholder: t('table.filterSearch', { defaultValue: 'Search...' }), value: optionQuery, onChange: function (e) { return setOptionQuery(e.target.value); }, inputProps: {
                                'aria-label': t('table.filterSearch', { defaultValue: 'Search' }),
                                maxLength: MAX_INPUT_LENGTH,
                            } }), _jsx(Divider, {}), _jsxs(ValueList, __assign({ disablePadding: true }, { children: [_jsx(ValueItem, { control: _jsx(Checkbox, { size: "small", checked: selectAllChecked, indeterminate: selectAllIndeterminate, onChange: handleSelectAll }), label: _jsx("strong", { children: t('table.selectAll', { defaultValue: 'Select All' }) }) }), visibleValues.map(function (_a) {
                                    var value = _a.value, label = _a.label, count = _a.count;
                                    return (_jsx(ValueItem, { control: _jsx(Checkbox, { size: "small", checked: isValueChecked(value), onChange: function () { return handleToggleValue(value); } }), label: _jsxs(_Fragment, { children: [_jsx("span", __assign({ title: label !== null && label !== void 0 ? label : value, style: { overflow: 'hidden', textOverflow: 'ellipsis' } }, { children: label !== null && label !== void 0 ? label : value })), _jsxs(CountLabel, { children: ["(", count, ")"] })] }) }, value));
                                })] }))] })), kind === 'date' && (_jsxs(_Fragment, { children: [sortable && _jsx(Divider, {}), _jsx(FilterLabel, { children: t('table.filter', { defaultValue: 'Filter' }) }), _jsxs(DateRangeBox, { children: [_jsx(DatePicker, { label: t('table.filterFrom', { defaultValue: 'From' }), value: toMoment((_b = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.from) !== null && _b !== void 0 ? _b : null), maxDate: (_d = toMoment((_c = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.to) !== null && _c !== void 0 ? _c : null)) !== null && _d !== void 0 ? _d : undefined, onChange: function (value) { var _a; return onApply({ dateRange: { from: toDateString(value), to: (_a = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.to) !== null && _a !== void 0 ? _a : null } }); }, slotProps: { textField: { size: 'small', fullWidth: true } } }), _jsx(DatePicker, { label: t('table.filterTo', { defaultValue: 'To' }), value: toMoment((_e = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.to) !== null && _e !== void 0 ? _e : null), minDate: (_g = toMoment((_f = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.from) !== null && _f !== void 0 ? _f : null)) !== null && _g !== void 0 ? _g : undefined, onChange: function (value) { var _a; return onApply({ dateRange: { from: (_a = currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.from) !== null && _a !== void 0 ? _a : null, to: toDateString(value) } }); }, slotProps: { textField: { size: 'small', fullWidth: true } } }), ((currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.from) || (currentDateRange === null || currentDateRange === void 0 ? void 0 : currentDateRange.to)) && (_jsx(ClearRow, { children: _jsx(Button, __assign({ size: "small", onClick: function () { return onApply({ dateRange: null }); } }, { children: t('table.clear', { defaultValue: 'Clear' }) })) }))] })] }))] }) })));
};
